import ChartDataTable from '@/pages/CompanyReport/components/Overview/components/ChartDataTable';
import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import { EarningsRevenueTableWrapper } from './styled';
import { selector } from '../../selector';
import { shallowEqual, useSelector } from 'react-redux';
import Tooltip from '@/components/Tooltip';
import {
  Term,
  TooltipTitle,
} from '@/pages/CompanyReport/components/Overview/components/Fundamentals/styled';
import {
  OverviewTranslation,
  useOverviewTranslation,
} from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';
import type { ReactNode } from 'react';

const TTMTooltip = ({ children }: { children: ReactNode }) => {
  const t = useOverviewTranslation();
  return (
    <Tooltip
      title={
        <TooltipTitle>{t('fundamentals.revenue.table.tooltip')}</TooltipTitle>
      }
      light
      openDelay={300}
    >
      <Term>{children}</Term>
    </Tooltip>
  );
};

const EarningsRevenueTable = () => {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const t = useOverviewTranslation();

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t('fundamentals.revenue.table.heading', {
            tickerSymbol: selected?.tickerSymbol,
          }),
          about: t('fundamentals.revenue.table.description', {
            companyName: selected?.companyName,
          }),
        }}
      />
      <EarningsRevenueTableWrapper>
        <ChartDataTable
          data-cy-id="overview-earnings-revenue-table"
          columnTextAlignments="l"
        >
          <caption>
            {t('fundamentals.revenue.table.description', {
              companyName: selected?.companyName,
            })}
          </caption>
          <thead>
            <tr>
              <th colSpan={2}>
                {t('fundamentals.revenue.table.heading', {
                  tickerSymbol: selected?.tickerSymbol,
                })}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('fundamentals.revenue.table.marketCap')}</td>
              <td>{selected?.formattedMarketCap}</td>
            </tr>
            <tr>
              <td>
                <OverviewTranslation i18nKey="fundamentals.revenue.table.earnings">
                  Earnings (<TTMTooltip>TTM</TTMTooltip>)
                </OverviewTranslation>
              </td>
              <td>{selected?.formattedEarnings}</td>
            </tr>
            <tr>
              <td>
                <OverviewTranslation i18nKey="fundamentals.revenue.table.revenue">
                  Revenue (<TTMTooltip>TTM</TTMTooltip>)
                </OverviewTranslation>
              </td>
              <td>{selected?.formattedRevenue}</td>
            </tr>
          </tbody>
        </ChartDataTable>
      </EarningsRevenueTableWrapper>
    </>
  );
};

export default EarningsRevenueTable;
